// Vanilla CSS for MatchingActivityMaterialContent — V4 row-by-row guided pairing
// (design system AssignmentActivityScreenV4, aas__mp). Prefix: mat2-
// Built on Playful Frost tokens: --catchup-*, --brand-*, --color-success*/--color-error*,
// --fs-*, --rad-catchup-*, --space-*, --glass-*.
export const matchingActivityMaterialContentStyles = `
  .mat2-root { display: flex; flex-direction: column; gap: var(--space-4); }

  /* guide strip: instruction + n/N matched progress */
  .mat2-guide {
    display: flex; align-items: center; justify-content: space-between; gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--rad-catchup-large);
    background: var(--brand-50); border: 1px solid var(--catchup-blue-100);
  }
  .mat2-guidetext { font-size: var(--fs-base); font-weight: 700; color: var(--brand-dark); }
  .mat2-progress { flex-shrink: 0; font-size: var(--fs-sm); font-weight: 700; color: var(--text-muted); }

  /* term → pick rows */
  .mat2-rows { display: flex; flex-direction: column; gap: var(--space-3); }
  .mat2-row {
    display: grid; grid-template-columns: auto 1fr auto minmax(190px, 1.1fr); align-items: center; gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--rad-catchup-large);
    background: var(--catchup-white); border: 1.5px solid var(--catchup-blue-100); box-shadow: var(--shadow-card);
    transition: border-color var(--duration-base) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard);
  }
  .mat2-row.is-set { border-color: var(--catchup-blue-200); }
  /* NOTE: no position/z-index here — a z on the row would trap .mat2-menu
     (z 201) inside a low stacking context and let page chrome (left nav z-50)
     paint above it. The menu's own z beats the sibling rows (z auto) already. */
  .mat2-row.is-open { border-color: var(--catchup-blue); box-shadow: 0 0 0 3px var(--brand-100, rgba(87,194,211,0.22)); }
  .mat2-row.is-correct { border-color: var(--color-success); background: var(--color-success-bg); }
  .mat2-row.is-wrong { border-color: var(--color-error); background: var(--color-error-bg); }
  /* Left blank — amber, deliberately NOT the red of a wrong pick: the learner
     did not answer wrongly, they did not answer. The key still shows below. */
  .mat2-row.is-blank { border-color: var(--color-warning); background: var(--color-warning-bg); }

  .mat2-num {
    width: 26px; height: 26px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
    border-radius: var(--rad-catchup-full);
    background: var(--catchup-blue); color: #fff; font-size: var(--fs-sm); font-weight: 800;
  }
  .mat2-row.is-correct .mat2-num { background: var(--color-success); }
  .mat2-row.is-wrong .mat2-num { background: var(--color-error); }
  .mat2-row.is-blank .mat2-num { background: var(--color-warning); }
  .mat2-term { font-size: var(--fs-base); font-weight: 700; color: var(--text-primary); min-width: 0; overflow-wrap: anywhere; }
  .mat2-arrow { color: var(--text-muted); font-weight: 700; flex-shrink: 0; }

  /* per-row picker */
  .mat2-pickwrap { position: relative; min-width: 0; }
  .mat2-pick {
    display: flex; align-items: center; gap: var(--space-2); width: 100%; cursor: pointer; font-family: var(--font-sans); text-align: left;
    font-size: var(--fs-base); font-weight: 700; color: var(--brand-dark);
    padding: 10px var(--space-3);
    border-radius: var(--rad-catchup-large);
    background: var(--brand-50); border: 1.5px solid var(--catchup-blue-200);
    transition: border-color var(--duration-base) var(--ease-standard), box-shadow var(--duration-base) var(--ease-standard);
  }
  .mat2-pick:hover { border-color: var(--catchup-blue); }
  .mat2-pick.is-filled { background: var(--catchup-white); }
  .mat2-pick.is-correct, .mat2-pick.is-wrong, .mat2-pick.is-blank {
    border-color: transparent; background: transparent; box-shadow: none; cursor: default;
    padding-left: 0; padding-right: 0;
  }
  .mat2-pick.is-correct { color: var(--color-success-text); }
  .mat2-pick.is-wrong { color: var(--color-error-text); }
  .mat2-pick.is-blank { color: var(--color-warning-text); }
  .mat2-pick.is-blank .mat2-pick-ph { color: var(--color-warning-text); font-weight: 700; }
  .mat2-pick-val { flex: 1; min-width: 0; overflow-wrap: anywhere; }
  .mat2-pick-ph { color: var(--text-muted); font-weight: 600; }
  .mat2-caret { flex-shrink: 0; color: var(--text-muted); font-size: 12px; }
  .mat2-ic { flex-shrink: 0; font-weight: 800; }

  /* glass option menu */
  .mat2-menu {
    position: absolute; top: calc(100% + 6px); left: 0; right: 0; z-index: calc(var(--z-dropdown, 200) + 1);
    display: flex; flex-direction: column; gap: 2px; padding: var(--space-2);
    border-radius: var(--rad-catchup-large);
    /* No backdrop-filter — the option list scrolls inside this menu (max-height +
       overflow-y below), so a blur re-sampled it every scroll frame. Near-opaque
       fill instead; see STYLING.md "no blur on a scroll container". */
    background: var(--glass-popover-bg-strong);
    border: 1px solid var(--glass-border-top); box-shadow: var(--glass-popover-shadow);
    max-height: 260px; overflow-y: auto;
    animation: mat2-menu-in 0.14s var(--ease-standard);
  }
  /* near the bottom of the card the menu opens upward instead of being clipped */
  .mat2-menu.is-up { top: auto; bottom: calc(100% + 6px); animation-name: mat2-menu-in-up; }
  @keyframes mat2-menu-in-up { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes mat2-menu-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
  .mat2-opt {
    display: flex; align-items: center; gap: var(--space-2); width: 100%; text-align: left; cursor: pointer; font-family: var(--font-sans);
    font-size: var(--fs-base); font-weight: 600; color: var(--text-primary);
    padding: var(--space-2) var(--space-3);
    border: 1px solid transparent; border-radius: var(--rad-catchup-medium); background: transparent;
  }
  .mat2-opt:hover { background: var(--brand-50); }
  .mat2-opt.is-on { background: var(--brand-50); border-color: var(--catchup-blue); }
  .mat2-opt-text { flex: 1; min-width: 0; overflow-wrap: anywhere; }
  .mat2-opt-used { flex-shrink: 0; font-size: var(--fs-sm); font-weight: 800; color: var(--text-muted); background: var(--catchup-blue-50, #eef7f9); padding: 1px 7px; border-radius: var(--rad-catchup-full); }
  .mat2-opt-check { flex-shrink: 0; color: var(--catchup-blue); font-weight: 800; }

  /* stacked "Doğru" reveal under a wrong pick (V4 aas__corr) */
  .mat2-corr {
    display: inline-flex; flex-wrap: wrap; align-items: baseline; gap: 4px 6px; max-width: 100%; margin-top: var(--space-2);
    padding: 3px var(--space-2); border-radius: var(--rad-catchup-small); background: var(--color-success-bg);
  }
  .mat2-corr-tag { font-size: 9px; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-success-text); opacity: 0.85; }
  .mat2-corr-val { font-size: var(--fs-sm); font-weight: 800; color: var(--color-success-text); line-height: 1.35; overflow-wrap: anywhere; word-break: break-word; white-space: normal; }

  /* text & media inside terms / picks / options */
  .mat2-text { font-size: var(--fs-base); font-weight: inherit; }
  .mat2-text--bold { font-weight: 800; }
  .mat2-text--underline { text-decoration: underline; }
  .mat2-math { font-size: var(--fs-lg); }
  .mat2-media-wrap { max-width: 100%; max-height: 140px; overflow: hidden; border-radius: var(--rad-catchup-medium); }

  /* narrow screens: stack term over its picker (V4 @560px) */
  @media (max-width: 560px) {
    .mat2-row { grid-template-columns: auto 1fr; row-gap: var(--space-2); }
    .mat2-arrow { display: none; }
    .mat2-pickwrap { grid-column: 1 / -1; }
  }
`;
